import React, { useState, useEffect } from 'react';
import { plans, auth, userProfile } from '../api';

const PaymentSuccessPage = ({ setCurrentPage }) => {
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [message, setMessage] = useState('Verifying your payment...');
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session ID from URL (hash-based routing)
        const hash = window.location.hash.substring(1); // Remove the '#' character
        const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';
        const hashParams = new URLSearchParams(hashQuery);
        const sid = hashParams.get('session_id');
        
        setSessionId(sid);
        console.log('Extracted session ID:', sid);

        if (!sid) {
          setError('Missing payment information');
          setLoading(false);
          return;
        }

        // Verify the payment with Stripe
        const paymentResponse = await plans.verifyPayment(sid);
        
        if (paymentResponse.data.paid) {
          setPaymentVerified(true);
          setPlanId(paymentResponse.data.plan_id);
          
          // Check if the user is already logged in
          const token = localStorage.getItem('token');
          if (token) {
            // User is logged in, this was a plan switch/upgrade
            try {
              // Update the user's plan
              await auth.getCurrentUser(); // Make sure we're still logged in
              
              // Call the switch-plan endpoint
              await userProfile.switchPlan(paymentResponse.data.plan_id, sid);
              
              setMessage('Your plan has been successfully upgraded! Redirecting to dashboard...');
              
              // Fetch user profile again to make sure app state reflects the new plan
              try {
                await auth.getCurrentUser();
                
                // Redirect to dashboard after successful profile fetch with a full page reload
                // to ensure the user is properly recognized as logged in
                setTimeout(() => {
                  // Navigate to home by changing window.location
                  window.location.href = '/';
                }, 2000);
              } catch (error) {
                console.error('Error refreshing user profile after plan upgrade:', error);
                // Still redirect but user might need to refresh
                setTimeout(() => {
                  // Navigate to home by changing window.location
                  window.location.href = '/';
                }, 2000);
              }
            } catch (error) {
              console.error('Error updating plan:', error);
              setError('Payment was successful, but we could not update your plan. Please contact support.');
            }
          } else {
            // This was for a new registration - call the pre-registration endpoint
            try {
              // Call the pre-register endpoint to trigger email verification
              const response = await auth.preRegisterPayment(sid, paymentResponse.data.plan_id);
              
              // Check if the backend returned an auth token (for paid plans with auto-login)
              if (response.data.access_token) {
                // Save auth data to localStorage for immediate login
                localStorage.setItem('token', response.data.access_token);
                localStorage.setItem('user_id', response.data.user_id);
                localStorage.setItem('email', response.data.email);
                localStorage.setItem('is_admin', response.data.is_admin);
                
                setMessage('Payment successful! Your account has been created and you are now logged in.');
                
                // Fetch user profile before redirecting - this ensures the user appears logged in
                try {
                  await auth.getCurrentUser();
                  
                  // Redirect to dashboard after successful profile fetch with a full page reload
                  // to ensure the user is properly recognized as logged in
                  setTimeout(() => {
                    // Navigate to home by changing window.location
                    window.location.href = '/';
                  }, 2000);
                } catch (error) {
                  console.error('Error fetching user profile after payment:', error);
                  // Still redirect but user might need to refresh
                  setTimeout(() => {
                    // Navigate to home by changing window.location
                    window.location.href = '/';
                  }, 2000);
                }
              } else {
                // For free plans or if auto-login is not available
                // Save the session ID and plan ID to localStorage in case needed for later
                localStorage.setItem('checkout_session_id', sid);
                localStorage.setItem('selected_plan_id', paymentResponse.data.plan_id);
                
                setMessage(
                  'Payment successful! Your account has been registered and you can begin using the app right away. ' +
                  'We\'ve also sent a verification email to your inbox for your records. ' +
                  'You can log in now using the email and password you provided during registration.'
                );
              }
            } catch (error) {
              console.error('Error pre-registering user:', error);
              
              // Check if error is due to missing email
              if (error.response && 
                  error.response.data && 
                  error.response.data.detail && 
                  error.response.data.detail.includes('email')) {
                setMessage('Payment successful! Please provide your email to complete the registration process.');
                setShowEmailForm(true);
              } else {
                setError('Payment was successful, but we could not complete the pre-registration. Please contact support.');
              }
            }
            
            // No redirect - stay on this page to show the verification message
          }
        } else {
          setError(paymentResponse.data.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setError('An error occurred while verifying your payment. Please contact support.');
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [setCurrentPage]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save the email in metadata
      const response = await auth.completePaymentRegistration(sessionId, planId, email);
      
      // Check if the backend returned an auth token (for paid plans with auto-login)
      if (response.data.access_token) {
        // Save auth data to localStorage for immediate login
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user_id', response.data.user_id);
        localStorage.setItem('email', response.data.email);
        localStorage.setItem('is_admin', response.data.is_admin);
        
        setShowEmailForm(false);
        setMessage('Registration complete! Your account has been created and you are now logged in.');
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      } else {
        // For free plans or if auto-login is not available
        setShowEmailForm(false);
        setMessage(
          'Registration complete! Your account has been created and you can begin using the app right away. ' +
          'We\'ve sent a verification email to your inbox for your records. ' +
          'You can log in now using the email you provided.'
        );
      }
    } catch (error) {
      console.error('Error completing registration:', error);
      setError(error.response?.data?.detail || 'Failed to complete registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Payment Status
          </h2>
          
          {loading && (
            <div className="mt-6 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
              <p className="mt-4 text-lg text-gray-600">{message}</p>
            </div>
          )}
          
          {!loading && paymentVerified && !showEmailForm && (
            <div className="mt-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="mt-4 text-lg text-gray-600">{message}</p>
            </div>
          )}
          
          {!loading && showEmailForm && (
            <div className="mt-6">
              <p className="text-center text-lg text-gray-600 mb-4">{message}</p>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Your email address"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                  >
                    {isSubmitting ? 'Processing...' : 'Complete Registration'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {!loading && error && (
            <div className="mt-6 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <p className="mt-4 text-lg text-red-600">{error}</p>
              <div className="mt-6">
                <button
                  onClick={() => window.location.href = '/'}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
                >
                  Return to Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
